import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  DollarSign, 
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { notify } from '@/utils/notification';
import { format } from 'date-fns';

interface RefundManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  onStatusUpdate: () => void;
}

interface RefundHistoryItem {
  refund_status: string;
  refund_reason: string;
  refund_rejection_reason?: string;
  refund_requested_at?: string;
  refund_approved_at?: string;
  refund_processed_at?: string;
  price_at_booking: number;
  currency_at_booking: string;
  client_name: string;
  service_name: string;
}

export const RefundManagementModal: React.FC<RefundManagementModalProps> = ({
  isOpen,
  onClose,
  bookingId,
  onStatusUpdate
}) => {
  const [refundData, setRefundData] = useState<RefundHistoryItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionForm, setShowRejectionForm] = useState(false);

  const fetchRefundData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          refund_status,
          refund_reason,
          refund_rejection_reason,
          refund_requested_at,
          refund_approved_at,
          refund_processed_at,
          price_at_booking,
          currency_at_booking,
          service:services(service_name),
          client:clients(first_name, last_name)
        `)
        .eq('id', bookingId)
        .single();

      if (error) throw error;

      if (data) {
        setRefundData({
          ...data,
          client_name: `${data.client?.first_name || ''} ${data.client?.last_name || ''}`.trim() || 'Client',
          service_name: data.service?.service_name || 'Service'
        });
      }
    } catch (error) {
      console.error('Error fetching refund data:', error);
      notify.error('Error', 'Failed to load refund information');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && bookingId) {
      fetchRefundData();
    }
  }, [isOpen, bookingId]);

  const handleApproveRefund = async () => {
    try {
      setIsSubmitting(true);
      
      // First update the booking to mark it as approved with timestamp
      const { error: updateError } = await supabase
        .from('bookings')
        .update({
          refund_status: 'approved',
          refund_approved_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (updateError) {
        console.error('Error updating refund status:', updateError);
        notify.error("Update Error", "Failed to update refund status. Please try again.");
        return;
      }
      
      // Then call the stripe refund function which will handle processing
      const { data: refundData, error: refundError } = await supabase.functions.invoke('initiate-stripe-refund', {
        body: { bookingId }
      });
      
      if (refundError || (refundData && refundData.error)) {
        const msg = (refundError?.message || refundData?.error || '').toString();
        console.error('Error initiating refund:', refundError || refundData);
        if (/free/i.test(msg) && /cannot be refunded/i.test(msg)) {
          notify.error('Refund not allowed', 'This booking is free and cannot be refunded.');
        } else {
          notify.error('Refund processing error', 'Failed to process refund with Stripe. Please try again.');
        }
      } else {
        console.log('Refund initiated successfully:', refundData);
        
        // Update the booking with processed timestamp
        await supabase
          .from('bookings')
          .update({
            refund_processed_at: new Date().toISOString()
          })
          .eq('id', bookingId);
          
        notify.success("Refund Approved and Processed", "The refund request has been approved and processed through Stripe.");
      }

      onStatusUpdate();
      fetchRefundData(); // Refresh data
    } catch (error) {
      console.error('Error approving refund:', error);
      notify.error("Error", "Failed to approve refund request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectRefund = async () => {
    if (!rejectionReason.trim()) {
      notify.error("Required Field", "Please provide a reason for rejecting the refund");
      return;
    }

    try {
      setIsSubmitting(true);
      const { error } = await supabase
        .from('bookings')
        .update({
          refund_status: 'rejected',
          refund_rejection_reason: rejectionReason.trim()
        })
        .eq('id', bookingId);

      if (error) throw error;

      notify.success("Refund Rejected", "The refund request has been rejected.");
      setShowRejectionForm(false);
      setRejectionReason('');
      onStatusUpdate();
      fetchRefundData(); // Refresh data
    } catch (error) {
      console.error('Error rejecting refund:', error);
      notify.error("Error", "Failed to reject refund request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'requested': return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'approved': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'processed': return <DollarSign className="h-4 w-4 text-green-600" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      'requested': 'default',
      'approved': 'secondary',
      'rejected': 'destructive',
      'processed': 'outline'
    };
    
    return <Badge variant={variants[status] || 'outline'}>{status?.toUpperCase()}</Badge>;
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const getRefundTimeline = () => {
    if (!refundData) return [];
    
    const timeline = [];
    
    if (refundData.refund_requested_at) {
      timeline.push({
        step: 'Requested',
        date: refundData.refund_requested_at,
        icon: <Clock className="h-4 w-4 text-yellow-600" />,
        completed: true
      });
    }
    
    if (refundData.refund_approved_at) {
      timeline.push({
        step: 'Approved',
        date: refundData.refund_approved_at,
        icon: <CheckCircle className="h-4 w-4 text-green-600" />,
        completed: true
      });
    }
    
    if (refundData.refund_processed_at) {
      timeline.push({
        step: 'Processed',
        date: refundData.refund_processed_at,
        icon: <DollarSign className="h-4 w-4 text-green-600" />,
        completed: true
      });
    }
    
    return timeline;
  };

  const showActionButtons = refundData?.refund_status === 'requested';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Refund Management
          </DialogTitle>
          <DialogDescription>
            Manage refund requests and track refund progress for this booking.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading refund information...</span>
          </div>
        ) : refundData ? (
          <div className="space-y-6">
            {/* Booking Info */}
            <div className="bg-muted/30 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Booking Details</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="font-medium">Service:</span> {refundData.service_name}</div>
                <div><span className="font-medium">Client:</span> {refundData.client_name}</div>
                <div><span className="font-medium">Amount:</span> {formatPrice(refundData.price_at_booking, refundData.currency_at_booking)}</div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Status:</span> 
                  {getStatusBadge(refundData.refund_status)}
                </div>
              </div>
            </div>

            {/* Refund Request Details */}
            {refundData.refund_reason && (
              <div>
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Client's Refund Request
                </h3>
                <div className="bg-amber-50 p-3 rounded-md">
                  <p className="text-sm">{refundData.refund_reason}</p>
                </div>
              </div>
            )}

            {/* Rejection Reason */}
            {refundData.refund_rejection_reason && (
              <div>
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  Rejection Reason
                </h3>
                <div className="bg-red-50 p-3 rounded-md">
                  <p className="text-sm text-red-700">{refundData.refund_rejection_reason}</p>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Refund Timeline
              </h3>
              <div className="space-y-3">
                {getRefundTimeline().map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    {item.icon}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{item.step}</span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(item.date), 'MMM dd, yyyy HH:mm')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Action Buttons */}
            {showActionButtons && !showRejectionForm && (
              <div className="flex gap-2">
                <Button
                  onClick={handleApproveRefund}
                  disabled={isSubmitting}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Approve & Process Refund
                    </>
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowRejectionForm(true)}
                  disabled={isSubmitting}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject Refund
                </Button>
              </div>
            )}

            {/* Rejection Form */}
            {showRejectionForm && (
              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium">Reject Refund Request</h4>
                <Textarea
                  placeholder="Enter reason for rejection (required)"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleRejectRefund}
                    disabled={isSubmitting || !rejectionReason.trim()}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Rejecting...
                      </>
                    ) : (
                      'Reject Request'
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRejectionForm(false);
                      setRejectionReason('');
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Refresh Button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchRefundData}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No refund information available for this booking.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};